import styled from "styled-components";
import { Label, Input, Button } from 'reactstrap';
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import useSWR from "swr";
import { customFetcher, dateString, getTsDate, notify } from "../../../utils";
import { IFFM, SelectOption } from "../../../globals/interfaces";
import useUser from "../../../customHooks/useUser";
import apiClient from "../../../apiClient";
import Select from "../../../components/Select";
import { z } from 'zod';
import ModalConfirm from "./ModalConfirm";
import VirtualTable from "../../../components/custom/VirtualTable";

export default function ManageFlights() {

    const { user: { s_email } } = useUser();
    const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [flights, setFlights] = useState<Array<SelectOption>>([]);
    const [flightsMap, setFlightsMap] = useState<Record<string, Array<IFFM>>>({});
    const [selectedFlightId, setSelectedFlightId] = useState('');
    const [hard, setHard] = useState(false);
    const [modal, setModal] = useState(false);

    const getData = async () => {
        try {
            const res = await apiClient.get(`/dev-manage-flights/${date}`);
            const data: Array<IFFM> = res.data;
            const map: Record<string, Array<IFFM>> = {};
            data.map(item => {
                const { s_flight_id } = item;
                if (!map[s_flight_id]) {
                    map[s_flight_id] = [];
                }
                map[s_flight_id].push(item);
            });
            setFlightsMap(map);
            setFlights(Object.keys(map).map(key => ({ label: key.split('/')[0], value: key })));
            setSelectedFlightId('');
        } catch (err) {
            alert(err);
        }
    }

    useEffect(() => {
        getData();
    }, [date]);

    const launchDelete = (hard: boolean) => {
        setHard(hard);
        setModal(true);
    }

    const deleteFlight = async () => {
        const data = {
            s_flight_id: selectedFlightId,
            s_modified_by: s_email,
            t_modified: getTsDate(),
            b_hard: hard
        }
        const validationSchema = z.object({
            s_flight_id: z.string().min(1),
            s_modified_by: z.string().min(1),
            t_modified: dateString,
            b_hard: z.boolean()
        });


        try {
            validationSchema.parse(data);
            const res = await apiClient.post(`/delete-flight`, data);
            notify('Success');
            setModal(false);
            getData();
        } catch (err) {
            alert(err);
        }
    }

    return (
        <div>
            <FlexContainer>
                <div>
                    <Label>Date</Label>
                    <DateInput type={'date'} value={date} onChange={e => setDate(e.target.value)} />
                </div>
                <div>
                    <Label>Flights ({flights.length})</Label>
                    <Select
                        options={flights}
                        value={selectedFlightId}
                        onChange={(option) => setSelectedFlightId(String(option.value))}
                        width={150}
                    />
                </div>
                {
                    selectedFlightId && (
                        <div>
                            <Label>Actions</Label>
                            <FlexContainer>
                                <Button color={'warning'} onClick={() => launchDelete(false)}>Soft Delete</Button>
                                <Button color={'danger'} onClick={() => launchDelete(true)}>Hard Delete</Button>
                            </FlexContainer>
                        </div>
                    )
                }
            </FlexContainer>
            <div>
                <VirtualTable<IFFM> 
                    data={flightsMap[selectedFlightId]}
                    mapping={[{
                        name: 'Status',
                        value: 's_status'
                    }, {
                        name: 'Flight ID',
                        value: 's_flight_id'
                    }, {
                        name: 'Unique',
                        value: 'i_unique',
                        number: true
                    }, {
                        name: 'Sequence',
                        value: 'i_message_sequence',
                        number: true
                    }, {
                        name: 'Complete',
                        value: 'b_message_complete',
                        boolean: true
                    }, {
                        name: 'Continuation',
                        value: 'b_has_continuation',
                        boolean: true
                    }, {
                        name: 'POL',
                        value: 's_pol'
                    }, {
                        name: 'POU',
                        value: 's_pou'
                    }, {
                        name: 'Created by',
                        value: 's_created_by'
                    }, {
                        name: 'Modified by',
                        value: 's_modified_by'
                    }, {
                        name: 'Modified',
                        value: 't_modified',
                        datetime: true,
                        utc: true
                    }]}
                    numRows={10}
                />
            </div>
            <ModalConfirm 
                modal={modal}
                setModal={setModal}
                selectedFlightId={selectedFlightId}
                hard={hard}
                deleteFlight={deleteFlight}
            />
        </div>
    );
}

const FlexContainer = styled.div`
    display: flex;
    gap: 10px;
`;

const DateInput = styled(Input)`
    width: 150px;
`;